import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { createOfficeTask, getOfficeTasks, uploadOfficeTaskPhoto } from "../../../actions/officeTask.actions";
import {
    closeOfficeTaskCreateModal,
} from "../../../actions/officeTaskCreateModal.actions";
import { getOfficeMembers } from "../../../actions/officeMember.actions";
import { getOfficeOrders } from "../../../actions/officeOrder.actions";
import { getMaterials, getWarehouses } from "../../../actions/inventory.actions";
import { listOfficeProducts } from "../../../actions/productOffice.actions";
import TaskDueDatePicker from "./TaskDueDatePicker";
import {
    emitTasksChanged,
    fetchOrderSettings,
    fetchTaskGroups,
    uploadVoice,
} from "../tasks/officeTasksApi";
import useWebVoiceRecorder, { formatSeconds } from "../tasks/useWebVoiceRecorder";
import VoiceRecordBar from "../tasks/VoiceRecordBar";
import "../../../scss/adminOffice/components/office-tasks-AO.scss";

/** Простое окно (по умолчанию) или полное — запоминаем выбор в браузере. */
const MODE_KEY = "ao_task_create_mode";
const readMode = () => {
    try {
        return localStorage.getItem(MODE_KEY) === "full" ? "full" : "simple";
    } catch (_error) {
        return "simple";
    }
};
const storeMode = (value) => {
    try {
        localStorage.setItem(MODE_KEY, value);
    } catch (_error) { }
};

/** Конец дня через N дней — для кнопок «Сегодня» / «Завтра». */
const endOfDayIn = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    date.setHours(23, 59, 0, 0);
    return date.toISOString();
};

const sameMinute = (a, b) => {
    if (!a || !b) return false;
    return Math.abs(new Date(a).getTime() - new Date(b).getTime()) < 60000;
};


const PRIORITY = {
    low: "Низкий",
    normal: "Обычный",
    high: "Высокий",
    urgent: "Срочно",
};

const idOf = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;

    return String(value._id || value.id || value.userId?._id || value.userId || "");
};

const memberUser = (member) => member?.userId || member?.user || {};

const userName = (user = {}) => (
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
    user.email ||
    user.username ||
    "Сотрудник"
);

const safePromise = (value) => {
    if (value && typeof value.catch === "function") {
        value.catch(() => null);
    }
};

const makeEmptyForm = (prefill = {}) => {
    const links = prefill.links || {};

    return {
        title: String(prefill.title || ""),
        description: String(prefill.description || ""),
        priority: ["low", "normal", "high", "urgent"].includes(prefill.priority)
            ? prefill.priority
            : "normal",
        dueAt: String(prefill.dueAt || ""),
        assignmentType: ["person", "multiple", "board"].includes(prefill.assignmentType)
            ? prefill.assignmentType
            : "person",
        assigneeIds: Array.isArray(prefill.assigneeIds)
            ? prefill.assigneeIds.map(String).filter(Boolean)
            : [],
        checklistText: Array.isArray(prefill.checklist)
            ? prefill.checklist.map((item) => String(item?.text || item || "").trim()).filter(Boolean).join("\n")
            : String(prefill.checklistText || ""),
        orderId: String(links.orderId || prefill.orderId || ""),
        productId: String(links.productId || prefill.productId || ""),
        warehouseId: String(links.warehouseId || prefill.warehouseId || ""),
        materialId: String(links.materialId || prefill.materialId || ""),
        // Группа: "personal" — себе в «Мои задания», "general" — «Общие задания офиса», иначе id группы.
        listId: String(prefill.listId || "general"),
        flow: prefill.flow === "simple" ? "simple" : "review",
        workKey: "",
    };
};

/** Срок из «Настроек заказов»: через N дней, до конца дня. */
const dueFromDays = (days) => {
    const value = Number(days || 0);
    if (!value) return "";
    const date = new Date();
    date.setDate(date.getDate() + value);
    date.setHours(23, 59, 0, 0);
    return date.toISOString();
};

function getPortalTarget() {
    if (typeof document === "undefined") return null;

    return document.querySelector(".admin-app-AO") || document.body;
}

export default function GlobalOfficeTaskCreateModal() {
    const dispatch = useDispatch();

    const modal = useSelector((state) => state.officeTaskCreateModal || {}, shallowEqual);
    const taskState = useSelector((state) => state.officeTask || {}, shallowEqual);
    const { membersByOffice = {} } = useSelector((state) => state.officeMember || {}, shallowEqual);

    const orders = useSelector((state) => state.officeOrder?.orders || [], shallowEqual);
    const products = useSelector((state) => state.productOffice?.office || [], shallowEqual);
    const warehouses = useSelector((state) => state.inventory?.warehouses || [], shallowEqual);
    const materials = useSelector((state) => state.inventory?.materials || [], shallowEqual);

    const {
        open,
        officeId,
        source = "",
        sourceTitle = "",
        prefill = {},
        openedAt,
    } = modal;

    const [portalTarget, setPortalTarget] = useState(null);
    const [form, setForm] = useState(makeEmptyForm);
    const [referenceFiles, setReferenceFiles] = useState([]);
    const [error, setError] = useState("");
    const [groups, setGroups] = useState([]);
    const [works, setWorks] = useState([]);
    const [mode, setMode] = useState(readMode);
    // Голосовое к новому заданию: ляжет описанием, а без названия — название из расшифровки.
    const recorder = useWebVoiceRecorder();
    const [voice, setVoice] = useState(null);
    const [voiceSaving, setVoiceSaving] = useState(false);

    const cleanOfficeId = officeId && officeId !== "all" ? String(officeId) : "";
    const busy = !!taskState.actionLoading || voiceSaving;

    const members = useMemo(() => (
        (membersByOffice?.[cleanOfficeId] || []).filter((item) => (
            item?.isActive !== false &&
            item?.permissions?.tasks !== false
        ))
    ), [membersByOffice, cleanOfficeId]);

    const orderOptions = useMemo(() => {
        const result = [...orders];
        const prefillId = String(prefill?.links?.orderId || prefill?.orderId || "");

        if (prefillId && !result.some((item) => idOf(item) === prefillId)) {
            result.unshift({
                _id: prefillId,
                orderNumber: prefill?.links?.orderNumber || prefill?.orderNumber || `Заказ ${prefillId.slice(-6)}`,
            });
        }

        return result;
    }, [orders, prefill]);

    const productOptions = useMemo(() => {
        const result = [...products];
        const prefillId = String(prefill?.links?.productId || prefill?.productId || "");

        if (prefillId && !result.some((item) => idOf(item) === prefillId)) {
            result.unshift({
                _id: prefillId,
                name: prefill?.links?.productName || prefill?.productName || `Товар ${prefillId.slice(-6)}`,
                article: prefill?.links?.article || prefill?.article || "",
            });
        }

        return result;
    }, [products, prefill]);

    useEffect(() => {
        setPortalTarget(getPortalTarget());
    }, []);

    useEffect(() => {
        if (!open) return;

        setForm(makeEmptyForm(prefill));
        setReferenceFiles([]);
        setError("");
        setVoice(null);
        if (prefill?.mode === "full" || prefill?.mode === "simple") setMode(prefill.mode);
        else setMode(readMode());
        // «Надиктовать» на странице заданий: запись начинается сразу.
        if (prefill?.startVoice) recorder.start();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, openedAt, prefill]);

    // Окно закрыли во время записи — запись выбрасываем.
    useEffect(() => {
        if (!open && recorder.recording) recorder.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Группы заданий и «Работы по заказам» (быстрые кнопки).
    useEffect(() => {
        if (!open || !cleanOfficeId) return undefined;
        let alive = true;

        fetchTaskGroups(cleanOfficeId)
            .then((result) => alive && setGroups(result.lists))
            .catch(() => alive && setGroups([]));
        fetchOrderSettings(cleanOfficeId)
            .then((result) => alive && setWorks(result.saved ? result.templates.filter((item) => item.enabled) : []))
            .catch(() => alive && setWorks([]));

        return () => {
            alive = false;
        };
    }, [open, cleanOfficeId]);

    useEffect(() => {
        if (!open || !cleanOfficeId) return;

        safePromise(dispatch(getOfficeMembers(cleanOfficeId)));
        safePromise(dispatch(getOfficeOrders({
            officeId: cleanOfficeId,
            mode: "office",
            limit: 100,
        })));
        safePromise(dispatch(listOfficeProducts(cleanOfficeId)));
        safePromise(dispatch(getWarehouses(cleanOfficeId)));
        safePromise(dispatch(getMaterials({
            officeId: cleanOfficeId,
            scope: "all",
        })));
    }, [dispatch, open, cleanOfficeId]);

    useEffect(() => {
        if (!open || typeof document === "undefined") return undefined;

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event) => {
            if (event.key === "Escape" && !busy && !recorder.recording) {
                dispatch(closeOfficeTaskCreateModal());
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [dispatch, open, busy, recorder.recording]);

    if (!open || !portalTarget) return null;

    const personalTarget = form.listId === "personal";
    const sharedGroups = groups.filter((group) => group.kind === "shared");
    const selectedGroup = sharedGroups.find((group) => group._id === form.listId) || null;
    const linkedOrder = orderOptions.find((item) => idOf(item) === form.orderId);

    // Быстрая кнопка работы: заполняет форму по «Настройкам заказов».
    const applyWork = (work) => {
        const orderNumber = linkedOrder?.orderNumber || prefill?.links?.orderNumber || "";
        const assigneeIds = (work.assigneeIds || []).filter((id) => members.some((member) => idOf(memberUser(member)) === id));
        setForm((prev) => ({
            ...prev,
            workKey: work.key,
            title: `${work.title}${orderNumber ? ` · заказ ${orderNumber}` : ""}`.slice(0, 180),
            description: work.description || prev.description,
            priority: work.priority || "normal",
            flow: work.flow === "simple" ? "simple" : "review",
            listId: work.listId || "general",
            dueAt: dueFromDays(work.dueDays) || prev.dueAt,
            assignmentType: assigneeIds.length > 1 ? "multiple" : assigneeIds.length ? "person" : "board",
            assigneeIds,
            checklistText: (work.checklist || []).join("\n"),
        }));
    };

    const close = () => {
        if (busy || recorder.recording) return;
        dispatch(closeOfficeTaskCreateModal());
    };

    const switchMode = (next) => {
        setMode(next);
        storeMode(next);
        setError("");
    };

    // «Кому» в простом окне: себе (в «Мои задания»), на доску или одному сотруднику.
    const baseListId = prefill?.listId && prefill.listId !== "personal" ? String(prefill.listId) : "general";
    const whoValue = personalTarget
        ? "self"
        : form.assignmentType === "board"
            ? "board"
            : form.assigneeIds[0] || "";
    const setWho = (value) => setForm((prev) => {
        if (value === "self") return { ...prev, listId: "personal", assignmentType: "person", assigneeIds: [] };
        const listId = prev.listId === "personal" ? baseListId : prev.listId;
        if (value === "board") return { ...prev, listId, assignmentType: "board", assigneeIds: [] };
        return { ...prev, listId, assignmentType: "person", assigneeIds: value ? [value] : [] };
    });

    const toggleAssignee = (userId) => {
        setForm((prev) => {
            const id = String(userId);
            const exists = prev.assigneeIds.includes(id);

            if (prev.assignmentType === "person") {
                return {
                    ...prev,
                    assigneeIds: exists ? [] : [id],
                };
            }

            return {
                ...prev,
                assigneeIds: exists
                    ? prev.assigneeIds.filter((item) => item !== id)
                    : [...prev.assigneeIds, id],
            };
        });
    };

    const submit = async () => {
        setError("");

        if (!cleanOfficeId) {
            setError("Не найден выбранный офис.");
            return;
        }

        if (recorder.recording) {
            setError("Сначала закончите запись — нажмите «Готово».");
            return;
        }

        if (!form.title.trim() && !voice) {
            setError("Напишите, что сделать, или надиктуйте голосом.");
            return;
        }

        if (!personalTarget && form.assignmentType !== "board" && !form.assigneeIds.length) {
            setError("Выберите исполнителя или общую доску.");
            return;
        }

        const checklist = form.checklistText
            .split("\n")
            .map((text) => text.trim())
            .filter(Boolean)
            .slice(0, 30)
            .map((text) => ({ text }));

        const warehouse = warehouses.find((item) => idOf(item) === form.warehouseId);
        const material = materials.find((item) => idOf(item) === form.materialId);

        try {
            const created = await dispatch(createOfficeTask(cleanOfficeId, {
                title: form.title.trim(),
                // Без названия, но с голосом — название появится из расшифровки.
                ...(!form.title.trim() && voice ? { titleFromVoice: true } : {}),
                description: form.description.trim(),
                priority: form.priority,
                dueAt: form.dueAt || null,
                // «Мои задания» — всегда себе (сервер так и сделает).
                assignmentType: personalTarget ? "person" : form.assignmentType,
                assigneeIds: personalTarget || form.assignmentType === "board" ? [] : form.assigneeIds,
                listId: form.listId || "general",
                ...(prefill?.parentId ? { parentId: String(prefill.parentId) } : {}),
                ...(personalTarget ? {} : { flow: form.flow }),
                checklist,
                links: {
                    orderId: form.orderId,
                    productId: form.productId,
                    warehouseId: form.warehouseId,
                    materialId: form.materialId,
                    warehouseName: warehouse?.name || warehouse?.title || "",
                    materialName: material?.name || material?.title || material?.code || "",
                },
            }));

            const createdTaskId = idOf(created?.task);

            if (createdTaskId && voice) {
                setVoiceSaving(true);
                try {
                    await uploadVoice(cleanOfficeId, createdTaskId, voice, "task");
                } catch (voiceError) {
                    console.error("Не удалось загрузить голосовое к заданию:", voiceError);
                } finally {
                    setVoiceSaving(false);
                }
            }

            if (createdTaskId && referenceFiles.length) {
                for (const file of referenceFiles) {
                    try {
                        await dispatch(uploadOfficeTaskPhoto(
                            cleanOfficeId,
                            createdTaskId,
                            file,
                            "reference"
                        ));
                    } catch (photoError) {
                        console.error("Не удалось загрузить фото к созданному заданию:", photoError);
                    }
                }
            }

            dispatch(closeOfficeTaskCreateModal());
            emitTasksChanged({ officeId: cleanOfficeId, taskId: createdTaskId });
            safePromise(dispatch(getOfficeTasks({ officeId: cleanOfficeId })));
        } catch (requestError) {
            setError(
                requestError?.response?.data?.error ||
                requestError?.response?.data?.message ||
                requestError?.message ||
                "Не удалось создать задание."
            );
        }
    };

    const modalNode = (
        <div
            className="office-task-modal-AO office-task-global-create-AO"
            role="dialog"
            aria-modal="true"
            onMouseDown={close}
        >
            <section onMouseDown={(event) => event.stopPropagation()}>
                <header>
                    <div>
                        <span className="office-task-global-create-AO__eyebrow">
                            {sourceTitle || "Новое задание"}
                        </span>
                        <h2>{prefill?.parentId ? "Новое подзадание" : "Создать задание"}</h2>
                        <p>
                            {source === "order"
                                ? "Заказ уже привязан. Нажмите работу из списка или опишите задание сами."
                                : "Опишите, что сделать, выберите кому и к какому сроку."}
                        </p>
                    </div>
                    <div className="office-task-create-mode-AO" role="tablist" aria-label="Режим окна">
                        <button type="button" className={mode === "simple" ? "is-active" : ""} onClick={() => switchMode("simple")}>Быстрое</button>
                        <button type="button" className={mode === "full" ? "is-active" : ""} onClick={() => switchMode("full")}>Полное</button>
                    </div>
                    <button type="button" onClick={close} disabled={busy}>×</button>
                </header>

                <div className="office-task-modal-AO__content">
                    {mode === "simple" ? (
                        <div className="office-task-simple-AO">
                            {works.length && form.orderId ? (
                                <div className="office-task-v2-AO__works">
                                    <span>Работа по заказу — одним нажатием:</span>
                                    <div>
                                        {works.map((work) => (
                                            <button
                                                type="button"
                                                key={work.key}
                                                className={form.workKey === work.key ? "is-active" : ""}
                                                onClick={() => applyWork(work)}
                                            >
                                                {work.title}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            <label className="office-task-simple-AO__what">
                                <span>Что сделать</span>
                                <textarea
                                    autoFocus={!prefill?.startVoice}
                                    rows={3}
                                    maxLength={180}
                                    value={form.title}
                                    placeholder="Например: отполировать стелу к пятнице"
                                    onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) submit();
                                    }}
                                />
                            </label>

                            <div className="office-task-simple-AO__voice">
                                {voice && !recorder.recording ? (
                                    <div className="office-task-simple-AO__voice-ready">
                                        <b>🎤 Голосовое {formatSeconds(Math.round(voice.durationMs / 1000))}</b>
                                        <small>{form.title.trim() ? "Ляжет описанием задания" : "Название появится из расшифровки"}</small>
                                        <button type="button" className="office-task-v2-AO__link is-danger" onClick={() => setVoice(null)}>Удалить</button>
                                    </div>
                                ) : (
                                    <VoiceRecordBar recorder={recorder} label="Надиктовать" onFinished={setVoice} disabled={busy} />
                                )}
                                {recorder.error ? <div className="office-task-modal-AO__error">{recorder.error}</div> : null}
                            </div>

                            <div className="office-task-simple-AO__row">
                                <label>
                                    <span>Кому</span>
                                    <select value={whoValue} onChange={(event) => setWho(event.target.value)}>
                                        {!whoValue ? <option value="">Выберите, кому…</option> : null}
                                        {!prefill?.parentId ? <option value="self">Себе (мои задания)</option> : null}
                                        <option value="board">На доску — возьмёт любой</option>
                                        {members.map((member) => {
                                            const user = memberUser(member);
                                            return <option key={idOf(user)} value={idOf(user)}>{userName(user)}</option>;
                                        })}
                                    </select>
                                </label>

                                <div className="office-task-simple-AO__due">
                                    <span>Срок</span>
                                    <div>
                                        <button type="button" className={!form.dueAt ? "is-active" : ""} onClick={() => setForm((prev) => ({ ...prev, dueAt: "" }))}>Без срока</button>
                                        <button type="button" className={sameMinute(form.dueAt, endOfDayIn(0)) ? "is-active" : ""} onClick={() => setForm((prev) => ({ ...prev, dueAt: endOfDayIn(0) }))}>Сегодня</button>
                                        <button type="button" className={sameMinute(form.dueAt, endOfDayIn(1)) ? "is-active" : ""} onClick={() => setForm((prev) => ({ ...prev, dueAt: endOfDayIn(1) }))}>Завтра</button>
                                        <TaskDueDatePicker
                                            value={form.dueAt}
                                            disabled={busy}
                                            onChange={(dueAt) => setForm((prev) => ({ ...prev, dueAt }))}
                                        />
                                    </div>
                                </div>
                            </div>

                            {linkedOrder || selectedGroup ? (
                                <p className="office-task-modal-AO__hint">
                                    {[
                                        linkedOrder ? `Привязано к заказу ${linkedOrder.orderNumber || ""}` : "",
                                        selectedGroup ? `Группа «${selectedGroup.title}»` : "",
                                    ].filter(Boolean).join(" · ")}
                                </p>
                            ) : null}

                            <button type="button" className="office-task-simple-AO__more" onClick={() => switchMode("full")}>
                                Нужно больше: чек-лист, фото, приоритет, проверка, связи → «Полное»
                            </button>

                            {error ? <div className="office-task-modal-AO__error">{error}</div> : null}
                        </div>
                    ) : (
                    <>
                    {works.length && !personalTarget ? (
                        <div className="office-task-v2-AO__works">
                            <span>Быстро — работа из «Настроек заказов»:</span>
                            <div>
                                {works.map((work) => (
                                    <button
                                        type="button"
                                        key={work.key}
                                        className={form.workKey === work.key ? "is-active" : ""}
                                        onClick={() => applyWork(work)}
                                        title="Заполнит название, исполнителей, срок и чек-лист — можно поправить"
                                    >
                                        {work.title}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    <div className="office-task-modal-AO__grid">
                        <label>
                            <span>Куда</span>
                            <select
                                value={form.listId}
                                onChange={(event) => setForm((prev) => ({ ...prev, listId: event.target.value }))}
                            >
                                <option value="personal">Мои задания (себе)</option>
                                <option value="general">Общие задания офиса</option>
                                {sharedGroups.map((group) => (
                                    <option key={group._id} value={group._id}>{group.title}</option>
                                ))}
                            </select>
                        </label>

                        {!personalTarget ? (
                            <label>
                                <span>Проверка</span>
                                <select
                                    value={form.flow}
                                    onChange={(event) => setForm((prev) => ({ ...prev, flow: event.target.value }))}
                                >
                                    <option value="review">Руководитель принимает работу</option>
                                    <option value="simple">Без проверки — исполнитель сам ставит «Готово»</option>
                                </select>
                            </label>
                        ) : (
                            <p className="office-task-modal-AO__hint">Личное задание видите только вы, проверка не нужна.</p>
                        )}

                        <label className="is-wide">
                            <span>Название</span>
                            <input
                                autoFocus
                                value={form.title}
                                maxLength={180}
                                placeholder="Например: подготовить памятник к отгрузке"
                                onChange={(event) => setForm((prev) => ({
                                    ...prev,
                                    title: event.target.value,
                                }))}
                            />
                        </label>

                        <label className="is-wide">
                            <span>Описание</span>
                            <textarea
                                rows={4}
                                maxLength={4000}
                                value={form.description}
                                placeholder="Что нужно сделать, на что обратить внимание..."
                                onChange={(event) => setForm((prev) => ({
                                    ...prev,
                                    description: event.target.value,
                                }))}
                            />
                        </label>

                        <div className="is-wide office-task-simple-AO__voice">
                            {voice && !recorder.recording ? (
                                <div className="office-task-simple-AO__voice-ready">
                                    <b>🎤 Голосовое {formatSeconds(Math.round(voice.durationMs / 1000))}</b>
                                    <small>{form.title.trim() ? "Ляжет описанием задания" : "Название появится из расшифровки"}</small>
                                    <button type="button" className="office-task-v2-AO__link is-danger" onClick={() => setVoice(null)}>Удалить</button>
                                </div>
                            ) : (
                                <VoiceRecordBar recorder={recorder} label="Описание голосом" onFinished={setVoice} disabled={busy} />
                            )}
                            {recorder.error ? <div className="office-task-modal-AO__error">{recorder.error}</div> : null}
                        </div>

                        <label>
                            <span>Приоритет</span>
                            <select
                                value={form.priority}
                                onChange={(event) => setForm((prev) => ({
                                    ...prev,
                                    priority: event.target.value,
                                }))}
                            >
                                {Object.entries(PRIORITY).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </label>

                        <label>
                            <span>Срок</span>
                            <TaskDueDatePicker
                                value={form.dueAt}
                                disabled={busy}
                                onChange={(dueAt) => setForm((prev) => ({
                                    ...prev,
                                    dueAt,
                                }))}
                            />
                        </label>
                    </div>

                    {!personalTarget ? (
                    <div className="office-task-modal-AO__block">
                        <h3>Кому выдать</h3>
                        {selectedGroup?.visibility === "members" ? (
                            <p className="office-task-modal-AO__hint">
                                Группа «{selectedGroup.title}» — только для участников: выбирайте исполнителей из них.
                            </p>
                        ) : null}

                        <div className="office-task-modal-AO__types">
                            {[
                                ["person", "Одному"],
                                ["multiple", "Нескольким"],
                                ["board", "На общую доску"],
                            ].map(([key, label]) => (
                                <button
                                    type="button"
                                    key={key}
                                    className={form.assignmentType === key ? "is-active" : ""}
                                    onClick={() => setForm((prev) => ({
                                        ...prev,
                                        assignmentType: key,
                                        assigneeIds: key === "board"
                                            ? []
                                            : key === "person"
                                                ? prev.assigneeIds.slice(0, 1)
                                                : prev.assigneeIds,
                                    }))}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {form.assignmentType === "board" ? (
                            <p className="office-task-modal-AO__hint">
                                Первый сотрудник, который нажмёт «Взять», закрепит задание за собой.
                            </p>
                        ) : (
                            <div className="office-task-modal-AO__members">
                                {members.map((member) => {
                                    const user = memberUser(member);
                                    const userId = idOf(user);
                                    const selected = form.assigneeIds.includes(userId);

                                    return (
                                        <button
                                            type="button"
                                            key={userId}
                                            className={selected ? "is-selected" : ""}
                                            onClick={() => toggleAssignee(userId)}
                                        >
                                            <b>{userName(user)}</b>
                                            <small>{user.email || user.username || member.role || ""}</small>
                                            <i>{selected ? "✓" : ""}</i>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    ) : null}

                    <div className="office-task-modal-AO__block">
                        <h3>Связать с работой офиса</h3>

                        <div className="office-task-modal-AO__grid">
                            <label>
                                <span>Заказ</span>
                                <select
                                    value={form.orderId}
                                    onChange={(event) => setForm((prev) => ({
                                        ...prev,
                                        orderId: event.target.value,
                                    }))}
                                >
                                    <option value="">Не привязывать</option>
                                    {orderOptions.map((item) => (
                                        <option key={idOf(item)} value={idOf(item)}>
                                            {item.orderNumber || idOf(item)}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Товар</span>
                                <select
                                    value={form.productId}
                                    onChange={(event) => setForm((prev) => ({
                                        ...prev,
                                        productId: event.target.value,
                                    }))}
                                >
                                    <option value="">Не привязывать</option>
                                    {productOptions.map((item) => (
                                        <option key={idOf(item)} value={idOf(item)}>
                                            {item.article ? `${item.article} · ` : ""}
                                            {item.fullName || item.name || "Товар"}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Склад</span>
                                <select
                                    value={form.warehouseId}
                                    onChange={(event) => setForm((prev) => ({
                                        ...prev,
                                        warehouseId: event.target.value,
                                    }))}
                                >
                                    <option value="">Не привязывать</option>
                                    {warehouses.map((item) => (
                                        <option key={idOf(item)} value={idOf(item)}>
                                            {item.name || item.title || "Склад"}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Материал</span>
                                <select
                                    value={form.materialId}
                                    onChange={(event) => setForm((prev) => ({
                                        ...prev,
                                        materialId: event.target.value,
                                    }))}
                                >
                                    <option value="">Не привязывать</option>
                                    {materials.map((item) => (
                                        <option key={idOf(item)} value={idOf(item)}>
                                            {item.name || item.title || item.code || "Материал"}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </div>

                    <div className="office-task-modal-AO__block">
                        <h3>Фото к заданию</h3>

                        <label className="office-task-global-create-AO__file-picker">
                            <span>
                                Можно сразу приложить исходные фото. Исполнитель потом добавит свой фотоотчёт.
                            </span>
                            <input
                                type="file"
                                multiple
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(event) => {
                                    const files = Array.from(event.target.files || []).slice(0, 10);
                                    event.target.value = "";
                                    setReferenceFiles(files);
                                }}
                            />
                        </label>

                        {referenceFiles.length ? (
                            <div className="office-task-global-create-AO__file-list">
                                {referenceFiles.map((file, index) => (
                                    <span key={`${file.name}-${file.size}-${index}`}>
                                        {file.name}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                    </div>

                    <div className="office-task-modal-AO__block">
                        <h3>Чек-лист</h3>

                        <label>
                            <span>Один пункт на строку</span>
                            <textarea
                                rows={6}
                                value={form.checklistText}
                                placeholder={"Забрать комплект со склада\nПроверить размеры\nСделать фотоотчёт"}
                                onChange={(event) => setForm((prev) => ({
                                    ...prev,
                                    checklistText: event.target.value,
                                }))}
                            />
                        </label>
                    </div>

                    {error ? (
                        <div className="office-task-modal-AO__error">
                            {error}
                        </div>
                    ) : null}
                    </>
                    )}
                </div>

                <footer>
                    <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={close}
                        disabled={busy}
                    >
                        Отмена
                    </button>
                    <button
                        type="button"
                        className="btn btn--primary"
                        onClick={submit}
                        disabled={busy}
                    >
                        {voiceSaving ? "Загружаем голосовое…" : busy ? "Создаём..." : form.listId === "personal" ? "Добавить себе" : "Опубликовать"}
                    </button>
                </footer>
            </section>
        </div>
    );

    return createPortal(modalNode, portalTarget);
}
